import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  fetchHelpSupportOpenQuery,
  UdateHelpSupportOpenQuery,
  setPage,
  setPageSize,
  setFilters,
} from "../../../slices/helpSupportOpenSlice";
import api from "../../../auth/api";
import { alertSuccess, alertError } from "../../../components/Alert";
import Swal from "sweetalert2";
import Pagination from "../../../components/Pagination";
import PageSizeSelector from "../../../components/PageSizeSelector";
import EditableTable from "../../../components/EditableTable";
import Modal from "../../../components/Modal";
import Loader from "../../../components/Loader";
// import ChangeLog from "../../../components/ChangeLog";
// import AddQuery from "../Query/AddQuery";

const MaximizeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-4 h-4"
  >
    <path d="M15 3h6v6" />
    <path d="M9 21H3v-6" />
    <path d="M21 3l-7 7" />
    <path d="M3 21l7-7" />
  </svg>
);

const EmailTemplatePreview = ({ template, supportId, onClose, onBack, onResolve, isResolved, queryType, msg }) => {
  const iframeRef = React.useRef(null);
  const [templateData, setTemplateData] = useState(template);
  const [updating, setUpdating] = useState(false);
  const [lastEditedAt, setLastEditedAt] = useState(null);

  if (!templateData) return null;

  const handleFullscreen = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const iframe = iframeRef.current;
    if (iframe) {
      if (iframe.requestFullscreen) {
        iframe.requestFullscreen();
      } else if (iframe.webkitRequestFullscreen) {
        iframe.webkitRequestFullscreen();
      } else if (iframe.mozRequestFullScreen) {
        iframe.mozRequestFullScreen();
      } else if (iframe.msRequestFullscreen) {
        iframe.msRequestFullscreen();
      }
    }
  };

  const handleEditResponseClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    let oldMsg = templateData?.response_message || "";
    if (!oldMsg && templateData?.body) {
      const match = templateData.body.match(/\{\{([^}]+)\}\}/);
      if (match) {
        const text = match[1].trim();
        oldMsg = text === "query_response" ? "" : text;
      }
    }

    Swal.fire({
      title: "Edit Response Message",
      html: `
        <div style="text-align: left; font-size: 13px;">
          <div style="display: flex; gap: 8px; margin-bottom: 8px; align-items: center;">
            <span style="font-weight: bold; color: #4b5563; width: 85px; font-size: 11px; text-transform: uppercase; flex-shrink: 0;">Query Type:</span>
            <span style="padding: 4px 8px; background-color: #f3f4f6; border-radius: 4px; border: 1px solid #e5e7eb; font-weight: 500; color: #111827; font-size: 12px; flex-grow: 1;">${String(queryType || "N/A").trim()}</span>
          </div>
          <div style="display: flex; gap: 8px; margin-bottom: 12px; align-items: flex-start;">
            <span style="font-weight: bold; color: #4b5563; width: 85px; font-size: 11px; text-transform: uppercase; flex-shrink: 0; margin-top: 4px;">Message:</span>
            <span style="padding: 4px 8px; background-color: #f3f4f6; border-radius: 4px; border: 1px solid #e5e7eb; color: #111827; font-size: 12px; flex-grow: 1; white-space: pre-wrap; word-break: break-all; max-height: 80px; overflow-y: auto;">${String(msg || "N/A").trim()}</span>
          </div>
          <div>
            <strong style="color: #4b5563; font-size: 11px; text-transform: uppercase;">Response Message:</strong>
            <textarea id="swal-response-msg" class="swal2-textarea" style="margin: 4px 0 0 0; width: 100%; height: 70px; min-height: 60px; box-sizing: border-box; font-size: 13px; padding: 8px;" placeholder="Type response here...">${oldMsg}</textarea>
          </div>
        </div>  
      `,
      showCancelButton: true,
      confirmButtonText: "Update",
      confirmButtonColor: "#2563eb",
      cancelButtonText: "Cancel",
      didOpen: () => {
        const input = document.getElementById("swal-response-msg");
        const confirmButton = Swal.getConfirmButton();
        if (input && confirmButton) {
          confirmButton.disabled = !input.value.trim();
          input.addEventListener("input", (event) => {
            confirmButton.disabled = !event.target.value.trim();
          });
        }
      },
      preConfirm: () => {
        const responseMsg = document.getElementById("swal-response-msg").value;
        if (!responseMsg || !responseMsg.trim()) {
          Swal.showValidationMessage("Response message cannot be empty!");
          return false;
        }
        return responseMsg.trim();
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        setUpdating(true);
        try {
          const response = await api.patch("/HelpSupport/panel/Help/Open/email/template", {
            id: templateData?.id,
            response_message: result.value || "",
          });
          const updatedTemplate = response.data?.Email_Template || response.data;
          if (updatedTemplate) {
            setTemplateData(updatedTemplate);
            setLastEditedAt(new Date().toLocaleString());
            alertSuccess(response.data?.success || "Response message updated successfully");
          }
        } catch (err) {
          console.error(err);
          alertError(err.response?.data?.errors || err.message || "Failed to update response message");
        } finally {
          setUpdating(false);
        }
      }
    });
  };

  return (
    <div className="space-y-6 text-sm text-gray-700 max-w-2xl">
      {/* Header Info */}
      <div className="bg-gray-50 p-4 rounded-xl border border-gray-150 space-y-2">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
          Email Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">From</span>
            <span className="text-sm font-medium text-gray-800 mt-1">{templateData.email_label_from} &lt;{templateData.email_from}&gt;</span>
          </div>
          <div className="flex flex-col p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">To</span>
            <span className="text-sm font-medium text-gray-800 mt-1">{templateData.email_To}</span>
          </div>
          <div className="flex flex-col p-2 bg-white rounded-lg border border-gray-100 shadow-sm md:col-span-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Subject</span>
            <span className="text-sm font-medium text-gray-800 mt-1">{templateData.subject}</span>
          </div>
          {lastEditedAt && (
            <div className="flex flex-col p-2 bg-white rounded-lg border border-gray-100 shadow-sm md:col-span-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Response Message Edited At</span>
              <span className="text-sm font-medium text-gray-800 mt-1">{lastEditedAt}</span>
            </div>
          )}
        </div>
      </div>

      {/* HTML Email Body Container */}
      <div className="border border-gray-200 rounded-xl overflow-hidden shadow-inner bg-white">
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Email Template Body</span>
          <button
            type="button"
            onClick={handleFullscreen}
            title="Show email template in fullscreen"
            className="p-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg hover:text-gray-900 transition-colors flex items-center justify-center border border-gray-200 shadow-sm animate-pulse-once"
          >
            <MaximizeIcon />
          </button>
        </div>
        <iframe
          ref={iframeRef}
          srcDoc={templateData.body}
          title="Email Body Preview"
          className="w-full h-[450px] border-none"
        />
      </div>

      {/* Action button */}
      <div className="sticky bg-white pt-4 pb-4 border-t border-gray-100 flex justify-end gap-3 z-10 -mx-6 -mb-4 px-6" style={{ bottom: '-16px' }}>
        <button
          type="button"
          onClick={(e) => {
            if (e) {
              e.preventDefault();
              e.stopPropagation();
            }
            if (onBack) {
              onBack();
            } else {
              onClose();
            }
          }}
          className="px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg shadow-sm font-semibold transition-all duration-200"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleEditResponseClick}
          disabled={updating}
          className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg shadow-sm font-semibold transition-all duration-200"
        >
          {updating ? "Updating..." : "Edit Response Message"}
        </button>
        {!isResolved && !!(
          templateData?.response_message?.trim() ||
          templateData?.has_response_message ||
          templateData?.Email_Template?.response_message?.trim()
        ) && (
          <button
            type="button"
            onClick={async (e) => {
              if (e) {
                e.preventDefault();
                e.stopPropagation();
              }
              setUpdating(true);
              try {
                await onResolve(supportId);
              } catch (err) {
                console.error(err);
              } finally {
                setUpdating(false);
              }
            }}
            disabled={updating}
            className="px-5 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg shadow-sm font-semibold transition-all duration-200"
          >
            {updating ? "Resolving..." : "Resolve Query"}
          </button>
        )}
      </div>
    </div>
  );
};

const SupportDetailsView = ({ data, onProcess, onResolve, onClose }) => {
  const [emailTemplate, setEmailTemplate] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleProcessClick = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setLoading(true);
    try {
      const template = await onProcess(data.id);
      if (template) {
        setEmailTemplate(template);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditEmailTemplateClick = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setLoading(true);
    try {
      const response = await api.get(`/HelpSupport/panel/Help/Open/email/template?supportId=${data.id}`);
      const template = response.data?.Email_Template || response.data;
      if (template) {
        setEmailTemplate(template);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatKey = (key) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .trim()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const hiddenFields = ["id", "oldTrack", "Change_Log"];

  // Split details (read-only) and sort to put message fields at the end
  const detailEntries = Object.entries(data || {})
    .filter(([key]) => !hiddenFields.includes(key))
    .sort(([keyA], [keyB]) => {
      const isEndA = keyA === "msg" || keyA === "message" || keyA === "responseMsg" || keyA === "response_msg" || keyA === "response_message";
      const isEndB = keyB === "msg" || keyB === "message" || keyB === "responseMsg" || keyB === "response_msg" || keyB === "response_message";
      if (isEndA && !isEndB) return 1;
      if (!isEndA && isEndB) return -1;
      if (isEndA && isEndB) {
        if (keyA === "msg" || keyA === "message") return -1;
        if (keyB === "msg" || keyB === "message") return 1;
      }
      return 0;
    });

  if (emailTemplate) {
    return (
      <EmailTemplatePreview
        template={emailTemplate}
        supportId={data.id}
        onClose={onClose}
        onBack={() => setEmailTemplate(null)}
        onResolve={onResolve}
        isResolved={data?.queryResolve}
        queryType={data.queryType || data.query_type || data.QueryType || data.query}
        msg={data.msg || data.message || data.body}
      />
    );
  }

  return (
    <div className="space-y-6 text-sm text-gray-700 max-w-4xl w-full">
      {/* Dynamic Fields Section */}
      <div className="bg-gray-50 p-4 rounded-xl border border-gray-150">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
          Query Details
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {detailEntries.map(([key, val]) => {
            let displayVal = "";
            if (key === "UserId" || key === "userId" || key === "user_id") {
              displayVal = (
                <Link
                  to={`/panel-user/${val}`}
                  className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                >
                  {String(val ?? "")}
                </Link>
              );
            } else if (val === true) {
              displayVal = (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Yes
                </span>
              );
            } else if (val === false) {
              displayVal = (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  No
                </span>
              );
            } else {
              displayVal = <span className="font-medium break-all whitespace-pre-wrap">{String(val ?? "")}</span>;
            }

            const isLongText = String(val ?? "").length > 40;

            return (
              <div
                key={key}
                className={`flex flex-col p-3 bg-white rounded-lg border border-gray-100 shadow-sm ${
                  isLongText ? "col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-4" : ""
                }`}
              >
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  {formatKey(key)}
                </span>
                <div className="mt-1 text-gray-950 text-sm">{displayVal}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal Actions */}
      <div className="sticky bg-white pt-4 pb-4 border-t border-gray-100 flex justify-end gap-3 z-10 -mx-6 -mb-4 px-6" style={{ bottom: '-16px' }}>
        {!data?.queryInProgress ? (
          <>
            <button
              type="button"
              onClick={(e) => {
                if (e) {
                  e.preventDefault();
                  e.stopPropagation();
                }
                onClose();
              }}
              className="px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg shadow-sm font-semibold transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleProcessClick}
              disabled={loading}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg shadow-sm font-semibold transition-all duration-200"
            >
              {loading ? "Processing..." : "Process Query"}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={(e) => {
                if (e) {
                  e.preventDefault();
                  e.stopPropagation();
                }
                onClose();
              }}
              className="px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg shadow-sm font-semibold transition-all duration-200"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleEditEmailTemplateClick}
              disabled={loading}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg shadow-sm font-semibold transition-all duration-200"
            >
              {loading ? "Loading..." : "Edit Email Template"}
            </button>
            {!data?.queryResolve && !!(data?.has_response_message || data?.response_message?.trim()) && (
              <button
                type="button"
                onClick={async (e) => {
                  if (e) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                  setLoading(true);
                  try {
                    await onResolve(data.id);
                  } catch (err) {
                    console.error(err);
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="px-5 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg shadow-sm font-semibold transition-all duration-200"
              >
                {loading ? "Resolving..." : "Resolve Query"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const SupportList = () => {
  const dispatch = useDispatch();
  const {
    helpSupportQueryData,
    currentPage,
    pageSize,
    totalPages,
    inProgressFilter,
    isResolveFilter,
    search,
    status,
    error,
  } = useSelector((state) => state.helpSupportOpenQuery);

  const [selectedRow, setSelectedRow] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // Local state for search input
  const [searchVal, setSearchVal] = useState(search || "");

  // Sync local input with redux store search state
  useEffect(() => {
    setSearchVal(search);
  }, [search]);

  // Main useEffect to fetch paginated/filtered data
  useEffect(() => {
    dispatch(
      fetchHelpSupportOpenQuery({
        page: currentPage,
        page_size: pageSize,
        in_progress: inProgressFilter,
        is_resolve: isResolveFilter,
        search: search,
      })
    );
  }, [dispatch, currentPage, pageSize, inProgressFilter, isResolveFilter, search]);

  const handlePageChange = (page) => {
    dispatch(setPage(page));
  };

  const handlePageSizeChange = (size) => {
    dispatch(setPageSize(size));
  };

  const handleSearchSubmit = () => {
    dispatch(setFilters({ search: searchVal }));
  };

  const handleStatusChange = (val) => {
    if (val === "All") {
      dispatch(setFilters({ inProgressFilter: "", isResolveFilter: "" }));
    } else if (val === "Pending") {
      dispatch(setFilters({ inProgressFilter: "false", isResolveFilter: "false" }));
    } else if (val === "In progress") {
      dispatch(setFilters({ inProgressFilter: "true", isResolveFilter: "false" }));
    } else if (val === "Resolved") {
      dispatch(setFilters({ inProgressFilter: "", isResolveFilter: "true" }));
    }
  };

  const handleClearFilters = () => {
    setSearchVal("");
    dispatch(setFilters({ inProgressFilter: "", isResolveFilter: "", search: "" }));
  };

  const statusVal =
    inProgressFilter === "" && isResolveFilter === ""
      ? "All"
      : isResolveFilter === "true"
      ? "Resolved"
      : inProgressFilter === "true"
      ? "In progress"
      : "Pending";

  const handleView = (row) => {
    setSelectedRow(row);
    setIsViewModalOpen(true);
  };

  const handleProcessQuery = async (id) => {
    const resultAction = await dispatch(UdateHelpSupportOpenQuery({ id, queryInProgress: true }));
    if (UdateHelpSupportOpenQuery.fulfilled.match(resultAction)) {
      dispatch(
        fetchHelpSupportOpenQuery({
          page: currentPage,
          page_size: pageSize,
          in_progress: inProgressFilter,
          is_resolve: isResolveFilter,
          search: search,
        })
      );
      return resultAction.payload?.Email_Template;
    }
    return null;
  };

  const handleResolveQuery = async (id) => {
    const resultAction = await dispatch(UdateHelpSupportOpenQuery({ id, queryResolve: true }));
    if (UdateHelpSupportOpenQuery.fulfilled.match(resultAction)) {
      dispatch(
        fetchHelpSupportOpenQuery({
          page: currentPage,
          page_size: pageSize,
          in_progress: inProgressFilter,
          is_resolve: isResolveFilter,
          search: search,
        })
      );
      setIsViewModalOpen(false);
    }
  };

  const currentItems = Array.isArray(helpSupportQueryData.result) ? helpSupportQueryData.result : [];

  const headers =
    Array.isArray(helpSupportQueryData.result) && helpSupportQueryData.result.length > 0
      ? Object.keys(helpSupportQueryData.result[0]).filter((key) => key !== "id")
      : [];

  if (status === "loading") {
    return <Loader />;
  }

  const hasData = currentItems.length > 0;

  return (
    <div className="w-full flex items-center flex-col pt-4">
      {/* Search & Filter Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 w-11/12 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search by ID..."
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit()}
            className="block w-full px-3 py-2 border border-gray-200 rounded-xl bg-gray-50/50 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
          />
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Status:</span>
            <div className="flex bg-gray-100 p-1 rounded-xl border border-gray-200/40 shadow-inner">
              {["All", "Pending", "In progress", "Resolved"].map((statusOption) => {
                const isActive = statusVal === statusOption;
                return (
                  <button
                    key={statusOption}
                    type="button"
                    onClick={() => handleStatusChange(statusOption)}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all duration-200 active:scale-95 ${
                      isActive
                        ? "bg-white text-[var(--mainCol)] shadow-sm"
                        : "text-gray-500 hover:text-gray-900 hover:bg-white/50"
                    }`}
                  >
                    {statusOption}
                  </button>
                );
              })}
            </div>
          </div>

          {(searchVal || statusVal !== "All") && (
            <button
              onClick={handleClearFilters}
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-semibold"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="max-w-[98%] overflow-scroll flex justify-center w-full">
        {hasData ? (
          <EditableTable
            headers={headers}
            rows={currentItems}
            handleView={handleView}
            hideChangeLog={true}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <div className="text-5xl mb-3">🔍</div>
            <div className="text-lg font-medium">No Data Available</div>
            <div className="text-sm text-gray-400 mt-1">
              There are no records matching the criteria.
            </div>
          </div>
        )}
      </div>

      {hasData && (
        <div className="flex mt-4 items-center justify-between w-11/12">
          <PageSizeSelector
            pageSize={pageSize}
            setPageSize={handlePageSizeChange}
          />

          <Pagination
            currentPage={currentPage}
            setCurrentPage={handlePageChange}
            totalPages={totalPages}
          />
        </div>
      )}

      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title={selectedRow?.queryInProgress ? "Support Query Details" : "Process Support Query"}
        content={
          <SupportDetailsView
            data={selectedRow}
            onProcess={handleProcessQuery}
            onResolve={handleResolveQuery}
            onClose={() => setIsViewModalOpen(false)}
          />
        }
      />
    </div>
  );
};

export default SupportList;
